import { Link } from 'react-router-dom';
import { Shield, Users, ChevronRight } from 'lucide-react';
import { ROLE_LABEL } from './settingsRoleLabels';
import type { OrgRole } from '@core/types';

const ROLE_ORDER: OrgRole[] = ['owner', 'admin', 'senior_recruiter', 'junior_recruiter', 'viewer'];

const BLURB: Record<OrgRole, string> = {
  owner: 'Full control: billing, credits, team, and all recruitment areas.',
  admin: 'Manage team and settings; cannot purchase credits or change plan (unless also owner).',
  senior_recruiter: 'Full pipeline, Smart Match, reports, email — typical lead recruiter.',
  junior_recruiter: 'Create candidates and applications; work assigned roles and shared lists.',
  viewer: 'Read-mostly access for hiring managers or auditors; limited edits.',
};

/**
 * Phase 7 §2.9 — Plain-language RBAC overview. Actual role assignment happens under Team (org chart).
 */
export function RolesSettingsPage() {
  return (
    <div className="space-y-6">
      <div className="mb-1">
        <h2 className="text-[15px] font-semibold">Roles</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">Plain-language overview of role permissions. Assign roles from the Team page.</p>
      </div>
      <div className="ggj-param-group overflow-hidden">
        <div className="ggj-profile-section-h flex items-center gap-2">
          <Shield className="w-4 h-4 text-brand-400 shrink-0" />
          Roles & access
        </div>
        <div className="ggj-profile-section-body space-y-3">
          <p className="ggj-text-body vscode-text-secondary leading-relaxed">
            Each person has one <strong className="vscode-text-primary font-medium">role</strong> per organisation. It
            controls what appears in the sidebar (Applications, Reports, Billing, etc.) and what actions are allowed. To
            change someone’s role, open <strong className="vscode-text-primary font-medium">Team</strong> and select them
            in the org chart — you’ll see a preview of what they gain or lose before you confirm.
          </p>
        </div>
      </div>

      <div className="ggj-param-group overflow-hidden">
        <div className="ggj-profile-section-h">Role reference</div>
        <div className="ggj-profile-section-body p-0 divide-y vscode-border">
          {ROLE_ORDER.map((role) => (
            <div key={role} className="px-4 py-3.5 flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="w-44 shrink-0">
                <span className="ggj-text-body font-semibold vscode-text-primary">{ROLE_LABEL[role]}</span>
              </div>
              <p className="ggj-text-body vscode-text-secondary leading-relaxed min-w-0">{BLURB[role]}</p>
            </div>
          ))}
        </div>
      </div>

      <Link
        to="/settings/team"
        className="flex items-center justify-between gap-3 rounded-[10px] border vscode-border px-4 py-3.5 hover:vscode-bg-hover transition-colors"
      >
        <span className="flex items-center gap-2 ggj-text-body font-medium vscode-text-primary">
          <Users className="w-4 h-4 text-brand-400" />
          Open Team to assign roles
        </span>
        <ChevronRight className="w-4 h-4 vscode-text-tertiary shrink-0" />
      </Link>
    </div>
  );
}
